import type { TaskWithRelations } from '@vagusx/clients';
import { useEmailAttachments } from '@/hooks/use-emails';
import { useDocument } from '@/hooks/use-documents';

/**
 * Resolve the referral document a task is "about" so different overview
 * widgets (the referrer line in the header, the schedule-call drawer)
 * agree on a single source of truth.
 *
 * Resolution order:
 *   1. `task.objectType === 'document'` — `task.objectId` IS the document.
 *   2. `task.objectType === 'email'` — pick the first attachment whose
 *      extraction completed (has `documentId`).
 *
 * Returns the resolved id and, when present, the fetched document. Both
 * are nullable — neither widget should hard-error on tasks where the
 * extraction hasn't finished or there's no source document at all.
 */
export function useTaskReferralDocument(task: TaskWithRelations): {
  documentId: string | null;
  document: ReturnType<typeof useDocument>['data'] | null;
  isLoading: boolean;
} {
  const emailId = task.objectType === 'email' ? (task.objectId ?? undefined) : undefined;
  const attachmentsQuery = useEmailAttachments(emailId);

  const documentId =
    task.objectType === 'document' && task.objectId
      ? task.objectId
      : (attachmentsQuery.data?.find((a) => a.documentId)?.documentId ?? null);

  const documentQuery = useDocument(documentId ?? undefined);

  return {
    documentId,
    document: documentQuery.data ?? null,
    isLoading: attachmentsQuery.isLoading || documentQuery.isLoading,
  };
}
